import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { safePolygon, useClick, useDismiss, useFloatingRootContext, useHover, useInteractions, useRole } from '@floating-ui/react';
import { useControlled } from '../../utils/useControlled.js';
import { useEventCallback } from '../../utils/useEventCallback.js';
import { useTransitionStatus } from '../../utils/useTransitionStatus.js';
import { OPEN_DELAY } from '../utils/constants.js';
import { mergeProps } from '../../merge-props/index.js';
import { useOpenInteractionType } from '../../utils/useOpenInteractionType.js';
import { translateOpenChangeReason } from '../../utils/translateOpenChangeReason.js';
import { useOpenChangeComplete } from '../../utils/useOpenChangeComplete.js';
import { PATIENT_CLICK_THRESHOLD } from '../../utils/constants.js';
import { useScrollLock } from '../../utils/useScrollLock.js';
export function usePopoverRoot(params) {
  const {
    open: externalOpen,
    onOpenChange: onOpenChangeProp,
    defaultOpen = false,
    delay,
    closeDelay,
    openOnHover = false,
    onOpenChangeComplete,
    modal
  } = params;
  const delayWithDefault = delay ?? OPEN_DELAY;
  const closeDelayWithDefault = closeDelay ?? 0;
  const [instantType, setInstantType] = React.useState();
  const [titleId, setTitleId] = React.useState();
  const [descriptionId, setDescriptionId] = React.useState();
  const [triggerElement, setTriggerElement] = React.useState(null);
  const [positionerElement, setPositionerElement] = React.useState(null);
  const [openReason, setOpenReason] = React.useState(null);
  const [stickIfOpen, setStickIfOpen] = React.useState(true);
  const popupRef = React.useRef(null);
  const stickIfOpenTimeoutRef = React.useRef(-1);
  const [open, setOpenUnwrapped] = useControlled({
    controlled: externalOpen,
    default: defaultOpen,
    name: 'Popover',
    state: 'open'
  });
  const onOpenChange = useEventCallback(onOpenChangeProp);
  const {
    mounted,
    setMounted,
    transitionStatus
  } = useTransitionStatus(open);
  useScrollLock({
    enabled: open && modal === true && openReason !== 'hover',
    mounted,
    open,
    referenceElement: positionerElement
  });
  const setOpen = useEventCallback((nextOpen, event, reason) => {
    onOpenChange(nextOpen, event, reason);
    setOpenUnwrapped(nextOpen);
    if (nextOpen) {
      setOpenReason(reason ?? null);
    }
  });
  const handleUnmount = useEventCallback(() => {
    setMounted(false);
    setStickIfOpen(true);
    setOpenReason(null);
    onOpenChangeComplete?.(false);
  });
  useOpenChangeComplete({
    enabled: !params.actionsRef,
    open,
    ref: popupRef,
    onComplete() {
      if (!open) {
        handleUnmount();
      }
    }
  });
  React.useImperativeHandle(params.actionsRef, () => ({
    unmount: handleUnmount
  }), [handleUnmount]);
  const clearStickIfOpenTimeout = useEventCallback(() => {
    clearTimeout(stickIfOpenTimeoutRef.current);
  });
  React.useEffect(() => {
    if (!open) {
      clearStickIfOpenTimeout();
    }
  }, [clearStickIfOpenTimeout, open]);
  React.useEffect(() => {
    return clearStickIfOpenTimeout;
  }, [clearStickIfOpenTimeout]);
  const context = useFloatingRootContext({
    elements: {
      reference: triggerElement,
      floating: positionerElement
    },
    open,
    onOpenChange(openValue, eventValue, reasonValue) {
      const isHover = reasonValue === 'hover' || reasonValue === 'safe-polygon';
      const isKeyboardClick = reasonValue === 'click' && eventValue.detail === 0;
      const isDismissClose = !openValue && (reasonValue === 'escape-key' || reasonValue == null);
      function changeState() {
        setOpen(openValue, eventValue, translateOpenChangeReason(reasonValue));
      }
      if (isHover) {
        // Only allow "patient" clicks to close the popover if it's open.
        // If they clicked within 500ms of the popover opening, keep it open.
        setStickIfOpen(true);
        clearStickIfOpenTimeout();
        stickIfOpenTimeoutRef.current = window.setTimeout(() => {
          setStickIfOpen(false);
        }, PATIENT_CLICK_THRESHOLD);
        ReactDOM.flushSync(changeState);
      } else {
        changeState();
      }
      if (isKeyboardClick || isDismissClose) {
        setInstantType(isKeyboardClick ? 'click' : 'dismiss');
      } else {
        setInstantType(undefined);
      }
    }
  });
  const {
    openMethod,
    triggerProps
  } = useOpenInteractionType(open);
  const computedRestMs = delayWithDefault;
  const hover = useHover(context, {
    enabled: openOnHover && (openMethod !== 'touch' || openReason !== 'click'),
    mouseOnly: true,
    move: false,
    handleClose: safePolygon({
      blockPointerEvents: true
    }),
    restMs: computedRestMs,
    delay: {
      close: closeDelayWithDefault
    }
  });
  const click = useClick(context, {
    stickIfOpen
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);
  const {
    getReferenceProps,
    getFloatingProps: getPopupProps
  } = useInteractions([hover, click, dismiss, role]);
  const getTriggerProps = React.useCallback((externalProps = {}) => getReferenceProps(mergeProps(triggerProps, externalProps)), [getReferenceProps, triggerProps]);
  return React.useMemo(() => ({
    open,
    setOpen,
    mounted,
    setMounted,
    transitionStatus,
    setTriggerElement,
    positionerElement,
    setPositionerElement,
    popupRef,
    titleId,
    setTitleId,
    descriptionId,
    setDescriptionId,
    getTriggerProps,
    getPopupProps,
    floatingRootContext: context,
    instantType,
    openMethod,
    openReason,
    onOpenChangeComplete
  }), [open, setOpen, mounted, setMounted, transitionStatus, positionerElement, titleId, descriptionId, getTriggerProps, getPopupProps, context, instantType, openMethod, openReason, onOpenChangeComplete]);
}